// Considered: DeployApproachDialogOrganism — rejected because it deploys to an existing sphere, not creates a new org. See spec Reuse Audit.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  ChipAtom,
  CustomDialog,
  InputAtom,
  PrimeReactButton,
  useApiClientsOrNull,
} from 'design-system/my-lib';
import { ApproachDeploymentInput } from 'types/approach';
import OwnerPickerMolecule, { OwnerSelection } from './OwnerPickerMolecule';
import OrgTemplatePickerMolecule, { Template } from './OrgTemplatePickerMolecule';
import DeploymentInputsFormMolecule from './DeploymentInputsFormMolecule';
import './AddOrganizationDialogOrganism.scss';


export interface AddOrganizationDialogOrganismProps {
  visible: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

const AddOrganizationDialogOrganism: React.FC<AddOrganizationDialogOrganismProps> = ({
  visible,
  onHide,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const logAnalyticsEvent = useAnalytics();

  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState('');
  const [ownerSelection, setOwnerSelection] = useState<OwnerSelection | null>(null);
  const [selectedApproach, setSelectedApproach] = useState<Template | null>(null);
  const [deploymentInputValues, setDeploymentInputValues] = useState<Record<string, unknown>>({});
  const [approachInputs, setApproachInputs] = useState<ApproachDeploymentInput[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setStep(1);
      setName('');
      setOwnerSelection(null);
      setSelectedApproach(null);
      setDeploymentInputValues({});
      setApproachInputs([]);
      setSubmitting(false);
      setError(null);
    }
  }, [visible]);

  // The selected template's deploymentInputs come from the org-templates endpoint.
  // Cast to ApproachDeploymentInput[] — the backend shape is compatible.
  useEffect(() => {
    const inputs = (selectedApproach?.deploymentInputs ?? []) as ApproachDeploymentInput[];
    setApproachInputs(inputs);
    const seeded: Record<string, unknown> = {};
    for (const input of inputs) {
      if (input.default !== undefined) seeded[input.id] = input.default;
      else if (input.inputType === 'boolean') seeded[input.id] = false;
    }
    setDeploymentInputValues(seeded);
    setStep(1);
  }, [selectedApproach]);

  const hasRequiredInputs = approachInputs.some((input) => input.required);

  const requiredInputsSatisfied = approachInputs
    .filter((input) => input.required)
    .every((input) => {
      const value = deploymentInputValues[input.id];
      return value !== undefined && value !== null && value !== '';
    });

  const nameValid = name.trim().length > 0;
  // Step 1 with no required inputs, or step 2: Create. Required inputs must be
  // satisfied before Create is enabled.
  const canSubmit = nameValid && !submitting && (!hasRequiredInputs || requiredInputsSatisfied);
  const canAdvance = nameValid && !submitting;

  const handleCreate = useCallback(async () => {
    if (!apiClients?.organizationClient) return;
    setSubmitting(true);
    setError(null);

    const body: Record<string, unknown> = { name: name.trim() };
    if (ownerSelection?.mode === 'existing') {
      body.owner = { mode: 'existing', identityId: ownerSelection.identityId };
    } else if (ownerSelection?.mode === 'new') {
      body.owner = { mode: 'new', email: ownerSelection.email };
    }
    if (selectedApproach) {
      body.approach = {
        systemName: selectedApproach.systemName,
        // Foundation has channel: null (system approach, not sourced from any
        // channel); send '' so the required-string schema is satisfied and
        // downstream logs record "no channel origin."
        channelSystemName: selectedApproach.channel?.systemName ?? '',
        version: selectedApproach.version,
        deploymentInputValues,
      };
    }

    // Only the POST itself is treated as the create operation. Analytics /
    // parent-callback errors after a successful create must NOT be surfaced to
    // the user as "failed to create organization" — that produces the exact
    // false-negative EAINT-699 reports.
    let response: { owner?: { createdNewUser?: boolean }; approach?: { status?: string } };
    try {
      response = (await apiClients.organizationClient.post(
        '/admin/organizations',
        body
      )) as { owner?: { createdNewUser?: boolean }; approach?: { status?: string } };
    } catch (err) {
      // Log so a future repro surfaces the underlying cause in the console
      // without needing to spelunk backend logs. Do NOT strip: users can copy
      // it into a bug report.
      console.error('[AddOrganizationDialog] Failed to create organization', err);

      // Axios throws ECONNABORTED with no `.response` on timeout — and org
      // provisioning can take minutes end-to-end in slow environments even
      // when it ultimately succeeds. Distinguish that from a real backend
      // failure so we don't tell users to retry an op that may already be
      // in flight.
      const axiosErr = err as { code?: string; response?: unknown; message?: string };
      const isTimeout =
        axiosErr?.code === 'ECONNABORTED' ||
        (!axiosErr?.response && typeof axiosErr?.message === 'string' && /timeout/i.test(axiosErr.message));
      setError(
        t(
          isTimeout
            ? 'admin.organizations.addDialog.errors.createTimedOut'
            : 'admin.organizations.addDialog.errors.createFailed'
        )
      );
      setSubmitting(false);
      return;
    }

    // Post-success work — any failure here is logged but does not roll back
    // the user's perception of success (the org WAS created).
    try {
      logAnalyticsEvent('organization_created', undefined, {
        ownerMode: ownerSelection?.mode ?? 'self',
        hasApproach: !!selectedApproach,
        createdNewUser: response?.owner?.createdNewUser ?? false,
        ...(selectedApproach
          ? {
              approachSystemName: selectedApproach.systemName,
              channelSystemName: selectedApproach.channel?.systemName ?? '',
            }
          : {}),
        ...(response?.approach?.status ? { deployStatus: response.approach.status } : {}),
      });
    } catch (err) {
      console.warn('[AddOrganizationDialog] Post-create analytics failed (non-fatal)', err);
    }

    setSubmitting(false);
    onSuccess();
    onHide();
  }, [
    apiClients,
    name,
    ownerSelection,
    selectedApproach,
    deploymentInputValues,
    logAnalyticsEvent,
    onSuccess,
    onHide,
    t,
  ]);

  const handleNext = useCallback(() => setStep(2), []);
  const handleBack = useCallback(() => setStep(1), []);

  const handleDeploymentInputChange = useCallback(
    (inputId: string, value: unknown) => {
      setDeploymentInputValues((prev) => ({ ...prev, [inputId]: value }));
    },
    []
  );

  let ownerSummary: string;
  if (ownerSelection?.mode === 'existing') {
    ownerSummary =
      ownerSelection.displayName || ownerSelection.email || ownerSelection.identityId;
  } else if (ownerSelection?.mode === 'new') {
    ownerSummary = ownerSelection.email;
  } else {
    ownerSummary = t('admin.organizations.addDialog.owner.selfNote');
  }

  return (
    <CustomDialog
      visible={visible}
      onHide={onHide}
      header={t('admin.organizations.addDialog.title')}
    >
      <div className="add-org-dialog">
        {hasRequiredInputs && (
          <div className="add-org-dialog__steps" data-testid="step-indicator">
            <span
              className={`add-org-dialog__step-pill${step === 1 ? ' add-org-dialog__step-pill--active' : ''}`}
              data-testid="step-pill-1"
              aria-current={step === 1 ? 'step' : undefined}
            >
              <span className="add-org-dialog__step-num">1</span>
              <span className="add-org-dialog__step-name">
                {t('admin.organizations.addDialog.steps.template')}
              </span>
            </span>
            <span className="add-org-dialog__step-sep" aria-hidden="true" />
            <span
              className={`add-org-dialog__step-pill${step === 2 ? ' add-org-dialog__step-pill--active' : ''}`}
              data-testid="step-pill-2"
              aria-current={step === 2 ? 'step' : undefined}
            >
              <span className="add-org-dialog__step-num">2</span>
              <span className="add-org-dialog__step-name">
                {t('admin.organizations.addDialog.steps.configure')}
              </span>
            </span>
          </div>
        )}

        {step === 1 && (
          <div className="add-org-dialog__step" data-testid="org-form-step">
            <div className="add-org-dialog__field">
              <label className="add-org-dialog__label">
                {t('admin.organizations.addDialog.name.label')}
              </label>
              <InputAtom
                data-testid="org-name-input"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder={t('admin.organizations.addDialog.name.placeholder')}
                autoComplete="off"
              />
            </div>

            <div className="add-org-dialog__field">
              <label className="add-org-dialog__label">
                {t('admin.organizations.addDialog.owner.label')}
              </label>
              <OwnerPickerMolecule value={ownerSelection} onChange={setOwnerSelection} />
            </div>

            <div className="add-org-dialog__field">
              <div className="add-org-dialog__section-header" data-testid="template-section-header">
                <label className="add-org-dialog__label">
                  {t('admin.organizations.addDialog.approach.label')}
                </label>
                <ChipAtom
                  label={t('admin.organizations.addDialog.approach.optional')}
                  className="add-org-dialog__optional-badge"
                  data-testid="template-optional-badge"
                />
              </div>
              <p
                className="add-org-dialog__section-subtitle"
                data-testid="template-section-subtitle"
              >
                {t('admin.organizations.addDialog.approach.sectionSubtitle')}
              </p>
              <OrgTemplatePickerMolecule
                value={selectedApproach}
                onChange={setSelectedApproach}
              />
            </div>
          </div>
        )}

        {step === 2 && selectedApproach && (
          <div className="add-org-dialog__step" data-testid="deployment-inputs-step">
            <dl className="add-org-dialog__summary" data-testid="step-one-summary">
              <div className="add-org-dialog__summary-row">
                <dt className="add-org-dialog__summary-term">
                  {t('admin.organizations.addDialog.name.label')}
                </dt>
                <dd className="add-org-dialog__summary-desc" data-testid="summary-name">
                  {name}
                </dd>
              </div>
              <div className="add-org-dialog__summary-row">
                <dt className="add-org-dialog__summary-term">
                  {t('admin.organizations.addDialog.owner.label')}
                </dt>
                <dd className="add-org-dialog__summary-desc" data-testid="summary-owner">
                  {ownerSummary}
                </dd>
              </div>
              <div className="add-org-dialog__summary-row">
                <dt className="add-org-dialog__summary-term">
                  {t('admin.organizations.addDialog.approach.label')}
                </dt>
                <dd className="add-org-dialog__summary-desc" data-testid="summary-template">
                  {selectedApproach.displayName}
                </dd>
              </div>
            </dl>
            <p className="add-org-dialog__step-description">
              {t('admin.organizations.addDialog.deploymentInputs.description')}
            </p>
            <DeploymentInputsFormMolecule
              inputs={approachInputs}
              values={deploymentInputValues}
              onChange={handleDeploymentInputChange}
            />
          </div>
        )}

        {error && (
          <p className="add-org-dialog__error" data-testid="dialog-error" role="alert">
            {error}
          </p>
        )}

        {step === 1 && selectedApproach && (
          <div className="add-org-dialog__footer-strip" data-testid="footer-strip">
            {selectedApproach.channel ? (
              <>
                <i
                  className="pi pi-bolt add-org-dialog__footer-icon"
                  aria-hidden="true"
                />
                <span className="add-org-dialog__footer-text" data-testid="footer-strip-deploys">
                  {t('admin.organizations.addDialog.approach.footer.deploys', {
                    templateName: selectedApproach.displayName,
                    count: selectedApproach.counts.subApproaches,
                  })}
                </span>
              </>
            ) : (
              <>
                <i
                  className="pi pi-file add-org-dialog__footer-icon"
                  aria-hidden="true"
                />
                <span
                  className="add-org-dialog__footer-text"
                  data-testid="footer-strip-foundation"
                >
                  {t('admin.organizations.addDialog.approach.footer.foundation')}
                </span>
              </>
            )}
          </div>
        )}

        <div className="add-org-dialog__actions">
          {step === 2 && (
            <PrimeReactButton
              label={t('admin.organizations.addDialog.back')}
              onClick={handleBack}
              disabled={submitting}
              data-testid="back-button"
            />
          )}
          {step === 1 && hasRequiredInputs && (
            <PrimeReactButton
              label={t('admin.organizations.addDialog.next')}
              onClick={handleNext}
              disabled={!canAdvance}
              data-testid="next-button"
            />
          )}
          {(!hasRequiredInputs || step === 2) && (
            <PrimeReactButton
              label={
                submitting
                  ? t('admin.organizations.addDialog.creating')
                  : t('admin.organizations.addDialog.create')
              }
              onClick={handleCreate}
              disabled={!canSubmit}
              data-testid="create-button"
            />
          )}
        </div>
      </div>
    </CustomDialog>
  );
};

export default AddOrganizationDialogOrganism;
